import { useState } from "react";
import { OverlayTrigger, Popover, Modal, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

/**
 * Componente HelpTip - Exibe dicas explicativas
 * 
 * @param {string} title - Título da dica (opcional)
 * @param {string|ReactNode} content - Conteúdo da dica
 * @param {string} placement - Posição do popover (top, bottom, left, right)
 * @param {string} variant - Variante do ícone (info, help, warning)
 * @param {string} size - Tamanho do ícone (sm, md, lg)
 * @param {boolean} useModal - Se true, abre modal ao invés de popover
 * @param {string} modalSize - Tamanho do modal (sm, md, lg, xl)
 * @param {Array} tips - Lista de dicas para exibição em lista
 * @param {string} className - Classes CSS adicionais
 */
const HelpTip = ({
    title,
    content,
    placement = "top",
    variant = "info",
    size = "md",
    useModal = false,
    modalSize = "md",
    tips = [],
    className = "",
    children
}) => {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);

    const getIconClass = () => {
        const icons = {
            info: "fa-info-circle",
            help: "fa-question-circle",
            warning: "fa-exclamation-circle",
            lightbulb: "fa-lightbulb"
        };
        return icons[variant] || icons.info;
    };

    const getIconColor = () => {
        const colors = {
            info: "help-tip-primary",
            help: "help-tip-primary",
            warning: "text-warning",
            lightbulb: "text-warning"
        };
        return colors[variant] || colors.info;
    };

    const getSizeClass = () => {
        const sizes = {
            sm: "fs-6",
            md: "fs-5",
            lg: "fs-4"
        };
        return sizes[size] || sizes.md;
    };

    const renderContent = () => {
        if (tips && tips.length > 0) {
            return (
                <ul className="help-tip-list mb-0 ps-3">
                    {tips.map((tip, index) => (
                        <li key={index} className="mb-1">{tip}</li>
                    ))}
                </ul>
            );
        }
        return content;
    };

    const popover = (
        <Popover id="help-tip-popover" className="help-tip-popover">
            {title && (
                <Popover.Header as="h3" className="help-tip-header">
                    <i className={`fa ${getIconClass()} me-2`}></i>
                    {title}
                </Popover.Header>
            )}
            <Popover.Body className="help-tip-body">
                {renderContent()}
            </Popover.Body>
        </Popover>
    );

    const iconElement = (
        <span 
            className={`help-tip-icon ${getIconColor()} ${getSizeClass()} ms-1 ${className}`}
            style={{ cursor: "pointer" }}
            onClick={useModal ? () => setShowModal(true) : undefined}
        >
            <i className={`fa ${getIconClass()}`}></i>
        </span>
    );

    if (useModal) {
        return (
            <>
                {iconElement}
                <Modal 
                    show={showModal} 
                    onHide={() => setShowModal(false)}
                    size={modalSize}
                    centered
                    className="help-tip-modal"
                >
                    <Modal.Header closeButton className="help-tip-modal-header">
                        <Modal.Title style={{ color: '#ffffff' }}>
                            <i className={`fa ${getIconClass()} ${getIconColor()} me-2`}></i>
                            {title || t('help')}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="help-tip-modal-body">
                        {renderContent()}
                        {children}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            {t('close')}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }

    return (
        <OverlayTrigger
            trigger={["hover", "focus"]}
            placement={placement}
            overlay={popover}
        >
            {iconElement}
        </OverlayTrigger>
    );
};

/**
 * Componente HelpTipCard - Card explicativo com mais destaque
 * 
 * @param {string} title - Título do card
 * @param {string|ReactNode} content - Conteúdo do card
 * @param {string} variant - Variante de cor (info, success, warning, danger, primary)
 * @param {Array} tips - Lista de dicas
 * @param {boolean} dismissible - Se pode ser fechado
 * @param {boolean} collapsible - Se pode ser colapsado
 */
export const HelpTipCard = ({
    title,
    content,
    variant = "info",
    tips = [],
    dismissible = false,
    collapsible = false,
    defaultCollapsed = false,
    icon = "lightbulb",
    children
}) => {
    const [dismissed, setDismissed] = useState(false);
    const [collapsed, setCollapsed] = useState(defaultCollapsed);

    if (dismissed) return null;

    const getIcon = () => {
        const icons = {
            info: "fa-info-circle",
            help: "fa-question-circle",
            warning: "fa-exclamation-triangle",
            lightbulb: "fa-lightbulb",
            tip: "fa-hand-point-right",
            rocket: "fa-rocket",
            check: "fa-check-circle"
        };
        return icons[icon] || icons.lightbulb;
    };

    const renderContent = () => {
        if (tips && tips.length > 0) {
            return (
                <ul className="help-tip-card-list mb-0">
                    {tips.map((tip, index) => (
                        <li key={index} className="mb-2">
                            <i className="fa fa-check text-success me-2"></i>
                            {tip}
                        </li>
                    ))}
                </ul>
            );
        }
        return <p className="mb-0">{content}</p>;
    };

    return (
        <div className={`help-tip-card alert alert-${variant} border-0`}>
            <div className="help-tip-card-header d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                    <div className={`help-tip-card-icon bg-${variant} bg-opacity-25 rounded-circle p-2 me-3`}>
                        <i className={`fa ${getIcon()} text-${variant}`}></i>
                    </div>
                    {title && <h6 className="mb-0 fw-bold">{title}</h6>}
                </div>
                <div className="d-flex align-items-center gap-2">
                    {collapsible && (
                        <button 
                            className="btn btn-sm btn-link p-0"
                            onClick={() => setCollapsed(!collapsed)}
                        >
                            <i className={`fa fa-chevron-${collapsed ? 'down' : 'up'}`}></i>
                        </button>
                    )}
                    {dismissible && (
                        <button 
                            className="btn btn-sm btn-link p-0"
                            onClick={() => setDismissed(true)}
                        >
                            <i className="fa fa-times"></i>
                        </button>
                    )}
                </div>
            </div>
            {!collapsed && (
                <div className="help-tip-card-content mt-3">
                    {renderContent()}
                    {children}
                </div>
            )}
        </div>
    );
};

/**
 * Componente HelpTipInline - Dica inline ao lado de labels/títulos
 */
export const HelpTipInline = ({ text, placement = "top", variant = "info" }) => {
    return (
        <HelpTip 
            content={text} 
            placement={placement} 
            variant={variant} 
            size="sm" 
            className="ms-1"
        />
    );
};

export default HelpTip;
